import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Avatar from "@/components/Avatar";

/**
 * Avatar upload widget for the Account page. Optimistically updates the
 * `user.picture` via the provided onChange callback once the upload
 * succeeds, so the Navbar dropdown / admin list refresh on next render.
 */
export default function AvatarUploader({ user, onChange, testidPrefix = "account" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file (JPG / PNG / WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is too large (max 5MB).");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/users/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.picture);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't upload your photo. Try a different one.",
      );
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!user?.picture) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      await api.delete("/users/me/avatar");
      onChange?.(null);
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't remove your photo.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-5"
      data-testid={`${testidPrefix}-avatar-uploader`}
    >
      <div className="relative">
        <Avatar user={user} size="xl" testid={`${testidPrefix}-avatar`} />
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-white/70">
            <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">Profile photo</p>
        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
          Used on your résumé and shown next to your name in the admin area.
          A clear, friendly headshot works best. JPG / PNG / WebP · max 5MB.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid={`${testidPrefix}-avatar-input`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            data-testid={`${testidPrefix}-avatar-upload-button`}
          >
            <Camera className="h-3.5 w-3.5" />
            {user?.picture ? "Change photo" : "Upload photo"}
          </button>
          {user?.picture && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`${testidPrefix}-avatar-remove-button`}
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
